import { apiClient } from './apiClient';
import type { TtqSessionParticleEntry, TtqSubjectCode } from './ttqService';

export type CttqLifecycle = 'draft' | 'active' | 'superseded' | 'archived';
export type CttqReviewPriorityTier = 'primary' | 'deprioritized';

export interface CttqCoverageReport {
  overallScore: number;
  summary: string;
  computedAt: string;
  method: string;
}

export interface CttqRedundancyReport {
  redundancyScore: number;
  warnings: string[];
  suggestedAction: string;
  overlappingRecords: Array<{
    cttqRecordId: string;
    cttqParticleId: string;
    label?: string;
    memberSetJaccard: number;
    papOverlap?: number;
  }>;
  computedAt: string;
}

export interface ConvergingTtqRecord {
  id: string;
  ttqaId: string;
  cttqParticleId: string;
  convergedParticleIds: string[];
  coverageReport: CttqCoverageReport;
  lifecycle: CttqLifecycle;
  reviewPriorityTier: CttqReviewPriorityTier;
  redundancyReport?: CttqRedundancyReport | null;
  isPrimary?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ParticleConvergenceMembership {
  id: string;
  memberParticleId: string;
  cttqRecordId: string;
  cttqParticleId: string;
  membershipCoverageScore: number;
  networkIds: string[];
  cascadeOnComplete: boolean;
}

export interface CttqPortfolioHealth {
  subjectCode: TtqSubjectCode;
  activeCttqCount: number;
  eligibleMemberCount: number;
  convergenceRatio: number;
  avgMembersPerCttq: number;
  deprioritizedShare: number;
  alertLevel: 'ok' | 'warn' | 'critical';
}

export interface CttqPreviewResult {
  coverageReport: CttqCoverageReport;
  redundancyReport: CttqRedundancyReport;
  memberScores: Array<{ memberParticleId: string; score: number }>;
}

export interface MemberMembershipView {
  membership: ParticleConvergenceMembership;
  record: ConvergingTtqRecord | null;
}

export const cttqService = {
  listRecords(ttqaId: string) {
    return apiClient.getEnvelope<ConvergingTtqRecord[]>(
      `/api/ttq/sessions/${encodeURIComponent(ttqaId)}/cttq`,
    );
  },

  previewCreate(ttqaId: string, body: {
    cttqParticleId: string;
    convergedParticleIds: string[];
    networkIds?: string[];
  }) {
    return apiClient.postEnvelope<CttqPreviewResult>(
      `/api/ttq/sessions/${encodeURIComponent(ttqaId)}/cttq/preview`,
      body,
    );
  },

  createRecord(ttqaId: string, body: {
    cttqParticleId: string;
    convergedParticleIds: string[];
    userAcknowledgedRedundancy?: boolean;
    isPrimary?: boolean;
  }) {
    return apiClient.postEnvelope<{ record: ConvergingTtqRecord; memberships: ParticleConvergenceMembership[] }>(
      `/api/ttq/sessions/${encodeURIComponent(ttqaId)}/cttq`,
      body,
    );
  },

  getPortfolioHealth(subject: TtqSubjectCode) {
    return apiClient.getEnvelope<CttqPortfolioHealth>(
      `/api/cttq/portfolio-health?subject=${encodeURIComponent(subject)}`,
    );
  },

  listMemberMemberships(particleId: string) {
    return apiClient.getEnvelope<MemberMembershipView[]>(
      `/api/knowledge-particles/${encodeURIComponent(particleId)}/convergence-memberships`,
    );
  },

  completeTtqa(ttqaId: string, createAnchorIdentity = false) {
    return apiClient.postEnvelope<Record<string, unknown>>(
      `/api/ttq/sessions/${encodeURIComponent(ttqaId)}/complete`,
      { createAnchorIdentity },
    );
  },
};

export function resolveCttqAlertTone(alertLevel: CttqPortfolioHealth['alertLevel']): 'ok' | 'warn' | 'danger' {
  if (alertLevel === 'critical') return 'danger';
  if (alertLevel === 'warn') return 'warn';
  return 'ok';
}

export function formatCoveragePercent(score: number): string {
  return `${Math.round(Math.max(0, Math.min(1, score)) * 100)}%`;
}

export function pickDoneParticles(particles: TtqSessionParticleEntry[] = []) {
  return particles.filter((entry) => entry.pipelineStatus === 'done');
}
